import { createContext, useContext } from 'react';

export interface SessionUser {
  id: string;
  email: string;
  name?: string;
  /**
   * True when the user has confirmed TOTP enrolment. This is a TOTP-only
   * signal — registering a WebAuthn credential for MFA does not set it, which
   * is why the backend's step-up gate tests this and the credential count
   * separately. To know whether the user has a passkey, read
   * `GET /auth/webauthn/credentials`.
   */
  mfaEnabled: boolean;
  /**
   * True when the user authenticates solely via a federated IdP (no usable
   * local password). Local MFA/passkey management is disabled for these users.
   */
  localAuthManagedByIdp: boolean;
}
export interface SessionOrg {
  id: string;
  slug: string;
}
export interface SessionTenant {
  id: string;
  slug: string;
  name?: string;
}
/**
 * The actor behind an impersonation session. Present only when a privileged
 * caller (e.g. superadmin) is currently impersonating the {@link SessionUser}.
 * Detection requires the backend to surface an `impersonator` (or `act`)
 * field on `/auth/userinfo`; until that ships, this is always `null`.
 */
export interface SessionImpersonator {
  /** The acting admin's user ID (subject of the act claim). */
  id: string;
  /** Display email of the acting admin. */
  email: string;
}
export interface SessionState {
  user: SessionUser | null;
  /**
   * The organization the session is scoped to, from the `org_id` / `org_slug`
   * userinfo claims. `null` when the caller belongs to no organization, which
   * is a different state from belonging to one with no tenant — the console
   * shows different guidance for each.
   */
  org: SessionOrg | null;
  tenant: SessionTenant | null;
  roles: string[];
  /**
   * True when the backend's `OrgAdminUser` gate would admit this user (the
   * `is_org_admin` userinfo claim). Membership-derived, distinct from `roles`;
   * use this to gate org-admin-only surfaces. `false` when unauthenticated.
   */
  isOrgAdmin: boolean;
  /** Non-null while a privileged caller is acting as `user`. */
  impersonator: SessionImpersonator | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within <SessionProvider>');
  return ctx;
}
export function useUser(): SessionUser | null {
  return useSession().user;
}
export function useOrg(): SessionOrg | null {
  return useSession().org;
}
export function useTenant(): SessionTenant | null {
  return useSession().tenant;
}
export function useIsOrgAdmin(): boolean {
  return useSession().isOrgAdmin;
}
export function useImpersonator(): SessionImpersonator | null {
  return useSession().impersonator;
}
